import { AxiosResponse } from 'axios'
import {
  AuthUserProfile,
  ICreateOrganizationAndUserRequest,
  IForgotResetPassDto,
  ILoginRequest,
  ILoginResponse
} from 'types/auth'
import { ISignUpRequest } from 'types/auth'
import { Where } from 'types/common'
import { api } from 'API'
import { AuthRole } from 'interfaces/auth'
import { IOrganization } from 'interfaces/organization'
import { IUser } from 'interfaces/user'
import { IGroupMember } from '../../interfaces/group/index'
import config from '../../configs'

export async function signUp(user: ISignUpRequest): Promise<ILoginResponse> {
  const createdUser: AxiosResponse = await api.post('/auth/sign-up', user)
  return createdUser.data
}

export async function countDuplicatedSignedUpUsers(where: Where<IUser> = {}): Promise<number> {
  const user: AxiosResponse = await api.get('/auth/users/count', { params: { where } })
  return user?.data?.count ?? 0
}

export async function signUpOrganization(data: ICreateOrganizationAndUserRequest): Promise<IOrganization> {
  const createdOrganization: AxiosResponse = await api.post(`/auth/organizations`, data)
  return createdOrganization.data
}

export async function countOrganizationsForSignUp(where: Where<IOrganization> = {}): Promise<number> {
  const organization: AxiosResponse = await api.get(`/auth/organizations/count?where=${JSON.stringify(where)}`)
  return organization?.data?.count ?? 0
}

export async function login(login: ILoginRequest): Promise<ILoginResponse> {
  const response = await api.post(`/auth/login`, {
    username: login.username,
    email: login.email,
    password: login.password,
    organizationId: login.organizationId
  })
  return response.data as ILoginResponse
}

// SSO Authentication methods
export async function getOidcProviders(): Promise<{ providers: string[]; default: string }> {
  const response = await api.get('/auth/oidc/providers')
  return response.data
}

export function getOidcAuthUrl(provider: string, state?: string, isDemo: boolean = false, organizationId?: number): string {
  const stateParam = state || `react-${Date.now()}`
  
  // Use provider-specific authorization endpoint
  const endpoint = `/auth/oidc/${provider}/authorize`
  
  const params = new URLSearchParams()
  params.append('state', stateParam)
  if (isDemo) {
    params.append('isDemo', 'true')
  }
  if (organizationId) {
    params.append('organizationId', organizationId.toString())
  }
  
  return `${config.apiBaseUrl}${endpoint}?${params.toString()}`
}

export async function processOidcCallback(
  code: string, 
  state: string, 
  organizationId: number,
  provider: string
): Promise<ILoginResponse> {
  const endpoint = `/auth/oidc/${provider}/callback`
  const response = await api.post(endpoint, {
    code,
    state,
    organizationId
  })
  return response.data as ILoginResponse
}

// SSO User Auto-Creation
export async function createSsoUser(userData: {
  email: string
  organizationId: number
  firstName?: string
  lastName?: string
  username?: string
  ssoProvider?: string
}): Promise<{
  success: boolean
  message: string
  user: {
    id: number
    username: string
    email: string
    firstName: string
    lastName: string
    organizationId: number
  }
}> {
  const response = await api.post('/auth/sso-create-user', userData)
  return response.data
}

export async function getUserInfo(): Promise<AuthUserProfile> {
  const response = await api.get(`/users/me`)
  return response.data as AuthUserProfile
}

export async function getLoggedInUserGroupMembers(): Promise<IGroupMember[]> {
  try {
    const response = await api.get('/users/me/group-members')
    return response.data as IGroupMember[]
  } catch (error) {
    return []
  }
}

export function requestForgotPassword(email: string, subdomain: string) {
  return api.post(`/auth/forgot-password/init`, {
    email,
    subdomain
  })
}

export function finishForgotPassword(data: IForgotResetPassDto) {
  return api.post(`/auth/forgot-password/finish`, {
    ...data
  })
}

export async function getLoggingInOrganization(subdomain: string): Promise<{
  organization: IOrganization | null
  ssoOnlyAuth: boolean
}> {
  const response: AxiosResponse = await api.get(`/auth/organizations?subdomain=${subdomain}`)
  return response.data
}

export async function getPublicSSOConfigs(subdomain: string): Promise<{
  organizationId: number
  ssoConfigs: Array<{
    provider: string
    enabled: boolean
  }>
}> {
  const response: AxiosResponse = await api.get(`/auth/sso-configs?subdomain=${subdomain}`)
  return response.data
}

export async function getAuthRoles(): Promise<AuthRole[]> {
  const response: AxiosResponse = await api.get(`/auth-roles`)
  return response.data
}
